import React, {useEffect, useState} from 'react';
import axios from 'axios';
import {useLocation} from 'react-router-dom';
import {useTranslate} from "@tolgee/react";
import {FACEBOOK, FACEBOOK_GROUP, INSTAGRAM, PLATFORMS_COLORS} from "../../../../config/constants";
import Nav from 'react-bootstrap/Nav';
import FacebookPagesTable from "./tab";
import Alert from 'react-bootstrap/Alert';
import Button from 'react-bootstrap/Button';
import {Spinner} from "react-bootstrap";
import axiosClient from "../../../../utils/axios";

const FacebookTokenVerifier = () => {
    const TAB_DISCONNECTED_PROFILES = 'disconnectedProfiles';
    const TAB_FACEBOOK_PAGES = 'facebookPages';
    const TAB_INSTAGRAM_ACCOUNTS = 'instagramAccounts';

    const [selectedTab, setSelectedTab] = useState(TAB_DISCONNECTED_PROFILES);

    const [accessToken, setAccessToken] = useState(undefined);

    const [facebookPages, setFacebookPages] = useState([]);
    const [facebookPagesLoaded, setFacebookPagesLoaded] = useState(false);
    const [facebookPageCount, setFacebookPageCount] = useState(null);
    const [facebookPageLoadedCount, setFacebookPageLoadedCount] = useState(0);
    const [facebookPagesConnectedCount, setFacebookPagesConnectedCount] = useState(null);
    const [facebookPagesNotConnectedCount, setFacebookPagesNotConnectedCount] = useState(null);

    const [instagramAccounts, setInstagramAccounts] = useState([]);
    const [instagramAccountsLoaded, setInstagramAccountsLoaded] = useState(false);
    const [instagramAccountCount, setInstagramAccountCount] = useState(null);
    const [instagramAccountLoadedCount, setInstagramAccountLoadedCount] = useState(0);
    const [instagramAccountsConnectedCount, setInstagramAccountsConnectedCount] = useState(null);
    const [instagramAccountsNotConnectedCount, setInstagramAccountsNotConnectedCount] = useState(null);

    const [disconnectedProfiles, setDisconnectedProfiles] = useState([]);

    const [allDataLoaded, setAllDataLoaded] = useState(false);

    const [requestedPermissions, setRequestedPermissions] = useState(null);

    const [isRedirecting, setIsRedirecting] = useState(false);

    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const connectingSocialMediaUserUuid = queryParams.get('connecting_social_media_user_uuid');
    const successRedirectUrl = queryParams.get('success_redirect_url');
    const reconnectSocialMediaAccountUrl = queryParams.get('reconnect_social_media_account_url');

    const {t} = useTranslate();

    const startRedirecting = () => {
        setIsRedirecting(true);
        window.location.href = reconnectSocialMediaAccountUrl;
    };

    const hasPageAllPermissions = (id, platform) => {
        const permissions = Object.keys(accessToken.granular_scopes);

        return permissions.every((permission) => {
            const permissionName = accessToken.granular_scopes[permission].scope
            const targetIds = accessToken.granular_scopes[permission].target_ids

            if (requestedPermissions[platform].indexOf(permissionName) === -1) {
                return true;
            }

            if (!targetIds) {
                return true;
            }

            return targetIds.indexOf(id) > -1;
        })
    }

    const loadRequestedPermissions = async () => {
        if (requestedPermissions) {
            return;
        }
        return new Promise<any>(async (resolve, reject) => {
            const url = `/api/v1/platforms/facebook/requested_permissions/`;
            try {
                const response = await axios.get(url);
                setRequestedPermissions(JSON.parse(JSON.stringify(response.data)))
                resolve(response.data);
            } catch (error) {
                console.error('Error fetching requested permissions', error);
                reject(error)
            }
        })
    }

    const getDebugAccessToken = async () => {
        if (accessToken) {
            return;
        }
        return new Promise<any>(async (resolve, reject) => {
            const url = `/api/v1/platforms/facebook/debug_access_token/?connecting_social_media_user_uuid=${connectingSocialMediaUserUuid}`;
            try {
                const response = await axios.get(url);
                setAccessToken(JSON.parse(JSON.stringify(response.data)))
                resolve(response.data);
            } catch (error) {
                console.error('Error fetching debug access token', error);
                reject(error)
            }
        })
    };

    const getFacebookPages = async () => {
        if (facebookPagesLoaded) {
            return;
        }
        if (!accessToken) {
            return;
        }

        return new Promise<any>(async (resolve, reject) => {
            let finalResult = [];
            let url = `/api/v1/platforms/facebook/facebook_pages/?connecting_social_media_user_uuid=${connectingSocialMediaUserUuid}`;
            while (url) {
                try {
                    const response = await axios.get(url);
                    url = response.data.next;
                    setFacebookPageCount(response.data.count);
                    setFacebookPageLoadedCount(facebookPageLoadedCount + response.data.results.length);

                    finalResult = finalResult.concat(response.data.results);
                    for (let i = 0; i < finalResult.length; i++) {
                        finalResult[i]['hasPermissions'] = hasPageAllPermissions(finalResult[i].fb_id, FACEBOOK);
                    }
                    // Sort finalResult by hasPageAllPermissions and profile_name
                    finalResult.sort((a, b) => {

                        if (a.hasPermissions !== b.hasPermissions) {
                            // If one page has all permissions and the other doesn't, sort by hasPageAllPermissions
                            return a.hasPermissions ? 1 : -1;
                        } else {
                            // If both pages have the same permissions status, sort by profile_name
                            return a.profile_name.localeCompare(b.profile_name);
                        }
                    });

                    setFacebookPages(finalResult);

                } catch (error) {
                    console.error('Error fetching facebook pages', error);
                    reject(error)
                }
                setFacebookPagesLoaded(true)
                setFacebookPagesConnectedCount(finalResult.filter((page) => page.hasPermissions).length)
                setFacebookPagesNotConnectedCount(finalResult.filter((page) => !page.hasPermissions).length)

            }
        })
    };

    const getInstagramAccounts = async () => {
        if (instagramAccountsLoaded) {
            return;
        }
        if (!accessToken) {
            return;
        }

        return new Promise<any>(async (resolve, reject) => {
            let finalResult = [];
            let url = `/api/v1/platforms/facebook/instagram_accounts/?connecting_social_media_user_uuid=${connectingSocialMediaUserUuid}`;
            while (url) {
                try {
                    const response = await axios.get(url);
                    url = response.data.next;
                    setInstagramAccountCount(response.data.count);
                    setInstagramAccountsLoaded(facebookPageLoadedCount + response.data.results.length);

                    finalResult = finalResult.concat(response.data.results);
                    for (let i = 0; i < finalResult.length; i++) {
                        finalResult[i]['hasPermissions'] = hasPageAllPermissions(finalResult[i].fb_id, INSTAGRAM);
                    }

                    // Sort finalResult by hasPageAllPermissions and profile_name
                    finalResult.sort((a, b) => {
                        const aHasPermissions = hasPageAllPermissions(a, INSTAGRAM);
                        const bHasPermissions = hasPageAllPermissions(b, INSTAGRAM);

                        if (aHasPermissions !== bHasPermissions) {
                            // If one page has all permissions and the other doesn't, sort by hasPageAllPermissions
                            return aHasPermissions ? -1 : 1;
                        } else {
                            // If both pages have the same permissions status, sort by profile_name
                            return a.profile_name.localeCompare(b.profile_name);
                        }
                    });

                    setInstagramAccounts(finalResult);

                } catch (error) {
                    console.error('Error fetching facebook pages', error);
                    reject(error)
                }
                setInstagramAccountsLoaded(true)
                setInstagramAccountsConnectedCount(finalResult.filter((page) => page.hasPermissions).length)
                setInstagramAccountsNotConnectedCount(finalResult.filter((page) => !page.hasPermissions).length)
            }
        })
    }

    const updateAccessToken = async () => {
        if (!accessToken) {
            return;
        }
        return new Promise<any>(async (resolve, reject) => {
            const url = `/api/v1/platforms/facebook/update_access_tokens/?connecting_social_media_user_uuid=${connectingSocialMediaUserUuid}`;
            try {
                const response = await axiosClient.post(url);
                setAccessToken(JSON.parse(JSON.stringify(response.data)))
                window.location.href = successRedirectUrl;
                resolve(response.data);
            } catch (error) {
                console.error('Error fetching debug access token', error);
                reject(error)
            }
        })
    }

    const init = async () => {
    }

    useEffect(() => {
        getDebugAccessToken();
        loadRequestedPermissions()
    }, [])

    useEffect(() => {
        if (facebookPagesLoaded && instagramAccountsLoaded) {
            const disconnectedFacebookPages = facebookPages.filter(page => !page.hasPermissions);
            const disconnectedInstagramAccounts = instagramAccounts.filter(account => !account.hasPermissions);

            const disconnectedCombinedList = [...facebookPages, ...instagramAccounts].filter((page) => !page.hasPermissions);

            if (disconnectedCombinedList.length === 0) {
                updateAccessToken();

                return
            }

            setDisconnectedProfiles([...disconnectedProfiles, ...disconnectedFacebookPages, ...disconnectedInstagramAccounts]);


            setAllDataLoaded(true);
        }
    }, [facebookPagesLoaded, instagramAccountsLoaded]);

    useEffect(() => {
        if (accessToken && requestedPermissions) {
            getFacebookPages();
            getInstagramAccounts();
        }
    },)

    const columns = [
        {
            name: "",
            selector: row => (
                <img className={"img img-thumbnail"} src={row.profile_picture_url} style={{width: 50, height: 50}}/>
            )
        },
        {
            name: t("verifyFacebookAccessToken.platform"),
            selector: row => (
                <div className={'badge'}
                     style={{backgroundColor: PLATFORMS_COLORS[row.platform_name]}}>{row.platform_display_name}</div>
            )
        },
        {
            name: t("verifyFacebookAccessToken.name"),
            selector: row => (
                <b>{row.profile_name}</b>
            )
        },
        {
            name: t("verifyFacebookAccessToken.accessGranted"),
            selector: row => (
                <div>{row.hasPermissions ?
                    <div className={'badge bg-success'}>{t("verifyFacebookAccessToken.granted")}</div> :
                    <div className={'badge bg-danger'}>{t("verifyFacebookAccessToken.notGranted")}</div>}</div>
            )
        }
    ];

    return (
        <div className={"container"}>
            <div>
                {allDataLoaded && (
                    <div>
                        <div className={'d-flex mt-10'}>
                            <Alert className={"w-100"} show={true} variant="danger">
                                <Alert.Heading
                                    className={"text-danger"}>{t('verifyFacebookAccessToken.actionRequiredTitle')}</Alert.Heading>
                                <p>
                                    {t('verifyFacebookAccessToken.actionRequiredBody')}
                                </p>
                                <hr/>

                                <div className="d-flex justify-content-end">
                                    <Button variant="primary" onClick={startRedirecting} disabled={isRedirecting}>
                                        {isRedirecting ? <Spinner animation="border"
                                                                  size="sm"/> : t('verifyFacebookAccessToken.actionRequiredButton')}
                                    </Button>
                                </div>
                            </Alert>
                        </div>
                    </div>
                )}

                {!allDataLoaded && (
                    <div className={"d-flex justify-content-center flex-grow-1 align-items-center"}>
                        <div className={"bg-white px-10 py-10 rounded"}>
                            <div className={"h5 text-center"}>{t('verifyFacebookAccessToken.verifyingTokens')}</div>
                            <div className={"text-center"}>
                                <Spinner animation="border" role="status" variant="primary">
                                    <span className="visually-hidden">Loading...</span>
                                </Spinner>
                            </div>

                        </div>
                    </div>
                )}
                {allDataLoaded && (<div className="card card-flush h-md-100">
                    <div className="card-header pt-7">
                        <h3 className="card-title align-items-start flex-column">
                            <span
                                className="card-label fw-bold text-gray-800">{t('verifyFacebookAccessToken.connectedSocialAccounts')}</span>

                        </h3>

                    </div>
                    <div className="card-body pt-6">


                        <div className={''}>
                            <Nav variant="pills" activeKey={selectedTab}
                                 onSelect={(selectedKey) => setSelectedTab(selectedKey)}>
                                <Nav.Item>
                                    <Nav.Link eventKey={TAB_DISCONNECTED_PROFILES}>
                                        {t("verifyFacebookAccessToken.disconnectProfiles")}
                                    </Nav.Link>
                                </Nav.Item>
                                <Nav.Item>
                                    <Nav.Link eventKey={TAB_FACEBOOK_PAGES}>
                                        {t("verifyFacebookAccessToken.facebookPages")}
                                    </Nav.Link>
                                </Nav.Item>
                                <Nav.Item>
                                    <Nav.Link eventKey={TAB_INSTAGRAM_ACCOUNTS}>
                                        {t("verifyFacebookAccessToken.instagramAccounts")}
                                    </Nav.Link>
                                </Nav.Item>
                            </Nav>
                        </div>

                        {selectedTab === TAB_DISCONNECTED_PROFILES && (
                            <FacebookPagesTable
                                facebookPages={disconnectedProfiles}
                                facebookPagesConnectedCount={null}
                                facebookPagesNotConnectedCount={disconnectedProfiles.length}
                                columns={columns}
                            />

                        )}
                        {selectedTab === TAB_FACEBOOK_PAGES && (
                            <FacebookPagesTable
                                facebookPages={facebookPages}
                                facebookPagesConnectedCount={facebookPagesConnectedCount}
                                facebookPagesNotConnectedCount={facebookPagesNotConnectedCount}
                                columns={columns}
                            />

                        )}
                        {selectedTab === TAB_INSTAGRAM_ACCOUNTS && (
                            <FacebookPagesTable
                                facebookPages={instagramAccounts}
                                facebookPagesConnectedCount={instagramAccountsConnectedCount}
                                facebookPagesNotConnectedCount={instagramAccountsNotConnectedCount}
                                columns={columns}
                            />
                        )}
                    </div>
                </div>)}

            </div>
        </div>
    );
};

export default FacebookTokenVerifier;
